import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminWhoami } from "../../sim/admin";
import { track } from "../../sim/analytics";

/**
 * The "Play" call-to-action. For a NORMAL player it's just a link to /play on the current server.
 * For an ADMIN it becomes a split button: the main part plays THIS server, and a ▾ opens a small menu
 * to jump to the OTHER deployment's /play — so an admin can enter LIVE or TEST from either site.
 *
 * "Live" and "Test" are separate deployments at different URLs, so choosing the other one navigates
 * cross-origin (same tab) to that server's /play. The current server is marked with a ✓. Peer URLs come
 * from /api/admin/me (stagingUrl is set on live, liveUrl on test); if the peer URL isn't configured, we
 * quietly fall back to a plain Play link.
 *
 * `trackId` is the `cta_click` id sent on a click (null sends none), so each page's Play stays its own
 * line in the analytics.
 */
export function PlayButton({ label = "Play", className = "site-btn primary sm", trackId = "hero.play" }: { label?: string; className?: string; trackId?: string | null }) {
  const [info, setInfo] = useState<{ admin: boolean; env: "production" | "staging"; stagingUrl: string | null; liveUrl: string | null; maintenance: boolean } | null>(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    let alive = true;
    adminWhoami().then((r) => { if (alive) setInfo({ admin: r.admin, env: r.env, stagingUrl: r.stagingUrl, liveUrl: r.liveUrl, maintenance: r.maintenance }); });
    return () => { alive = false; };
  }, []);

  // close the menu on an outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const played = () => { if (trackId) track("cta_click", { id: trackId }); };
  const onStaging = info?.env === "staging";
  // The URL of the OTHER server's game (null if not configured on this deployment).
  const otherUrl = onStaging ? info?.liveUrl : info?.stagingUrl;

  // MAINTENANCE: when this server is in maintenance and the viewer isn't an admin, the game is closed —
  // grey out the button (non-clickable) and say why.
  if (info && info.maintenance && !info.admin) {
    return (
      <span className={`${className} play-maint`} role="button" aria-disabled="true"
        title="The game is temporarily down for maintenance. Please check back soon.">
        {label} (down for maintenance)
      </span>
    );
  }

  // Non-admin, still loading, or no peer URL configured → plain Play link to this server.
  if (!info || !info.admin || !otherUrl) {
    return <Link to="/play" className={className} onClick={played}>{label}</Link>;
  }

  const liveHref = onStaging ? `${otherUrl.replace(/\/$/, "")}/play` : "/play";
  const testHref = onStaging ? "/play" : `${otherUrl.replace(/\/$/, "")}/play`;

  return (
    <span className="play-split" ref={wrapRef}>
      <Link to="/play" className={`${className} play-split-main`} onClick={played}>{label}</Link>
      <button
        type="button"
        className={`${className} play-split-toggle`}
        aria-label="Choose server"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >▾</button>
      {open && (
        <div className="play-split-menu" role="menu">
          <a className="play-split-item" role="menuitem" href={liveHref} onClick={() => setOpen(false)}>
            <span className="play-split-ic">🌐</span> Live {!onStaging && <span className="play-split-here">✓</span>}
          </a>
          <a className="play-split-item" role="menuitem" href={testHref} onClick={() => setOpen(false)}>
            <span className="play-split-ic">🧪</span> Test {onStaging && <span className="play-split-here">✓</span>}
          </a>
        </div>
      )}
    </span>
  );
}
